/**
 * Installing the app, without leaving a service worker on the device of anyone who did
 * not ask for one.
 *
 * The dependency graph is not what it looks like. **Installing needs only the manifest** —
 * Safari's Add to Home Screen, and Chrome's own address-bar affordance since 108/112, work
 * with no service worker at all. A worker is needed by *push*, and by one convenience:
 * `beforeinstallprompt`, the Chromium-only event whose `prompt()` opens the native install
 * dialog from inside our page.
 *
 * That convenience is worth having — most people do not know a page can be installed, let
 * alone where the button is — but not at the price of registering a worker for every
 * visitor, which is a persistent registration that wakes on navigation and sits in front
 * of the SPA shell. So it is acquired **lazily and only on intent**: the worker is
 * registered when someone opens the install dialog, and torn down again if they walk away.
 *
 * The timing works because the flow has two clicks. Opening the dialog registers the
 * worker; reading the dialog covers the second or so it needs to activate and for Chrome
 * to re-run its installability check; clicking Install is a fresh gesture, which is what
 * `prompt()` requires — it needs transient activation, so a single click that registered
 * and then waited would likely have spent it.
 *
 * If any of that does not happen — Chrome declines to re-fire, Safari never fires at all —
 * the dialog shows platform instructions instead. That floor is the design we would
 * otherwise have shipped, so this cannot be worse than it.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { isStandalone } from './displayMode.ts';

/** Not in lib.dom — Chromium-only, and still not standardised. */
interface BeforeInstallPromptEvent extends Event {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed' }>;
}

export type InstallAvailability =
  /** Already running as an installed app. Nothing to offer. */
  | 'installed'
  /** Chromium: the native dialog is reachable, once a worker exists. */
  | 'promptable'
  /** iOS: installable, but only by hand through the Share sheet. */
  | 'ios-instructions'
  /** macOS Safari: installable by hand via File → Add to Dock. */
  | 'macos-instructions'
  /** Firefox desktop and anything else with no path to installing. */
  | 'unavailable';

export interface InstallPrompt {
  availability: InstallAvailability;
  /** True once the native dialog can actually be opened. */
  canPromptNow: boolean;
  /** Call when the install UI opens: acquires the worker, on Chromium only. */
  beginInstall: () => void;
  /** Call when the install UI closes without installing: releases what `beginInstall` took. */
  abandonInstall: () => void;
  /** Opens the browser's own install dialog. Only meaningful once `canPromptNow`. */
  promptInstall: () => Promise<'accepted' | 'dismissed' | 'unavailable'>;
}

/**
 * Whether this browser can ever fire `beforeinstallprompt`.
 *
 * Feature detection rather than a user-agent test, because this is exactly the capability
 * in question: the property exists on Chromium and not on Safari or Firefox. It also keeps
 * us from registering a worker on a browser where the event can never arrive — which is
 * every iOS browser, and iOS is where install matters most.
 */
function canEverPrompt(): boolean {
  return typeof window !== 'undefined' && 'onbeforeinstallprompt' in window;
}

function detectSafariPlatform(): InstallAvailability {
  if (typeof navigator === 'undefined') return 'unavailable';
  const ua = navigator.userAgent;

  // Every iOS browser is WebKit underneath and every one offers Add to Home Screen, so
  // this matches the platform rather than the browser. iPadOS reports a desktop UA.
  const isIos = /iPhone|iPad|iPod/.test(ua) || (/Macintosh/.test(ua) && navigator.maxTouchPoints > 1);
  if (isIos) return 'ios-instructions';

  const isSafari = /Safari\//.test(ua) && !/Chrome\/|Chromium\/|Edg\/|OPR\//.test(ua);
  if (isSafari && /Macintosh/.test(ua)) return 'macos-instructions';

  return 'unavailable';
}

export function useInstallPrompt(): InstallPrompt {
  const [deferred, setDeferred] = useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = useState(() => isStandalone());
  /** Whether *we* registered the worker, so we only ever tear down our own. */
  const acquired = useRef(false);

  useEffect(() => {
    const onBeforeInstall = (event: Event) => {
      // Suppress Chrome's own mini-infobar so the ask happens on our terms, and keep the
      // event — it is the only handle on the native dialog.
      event.preventDefault();
      setDeferred(event as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
      // Installed: the worker is theirs now, not something to clean up.
      acquired.current = false;
    };

    window.addEventListener('beforeinstallprompt', onBeforeInstall);
    window.addEventListener('appinstalled', onInstalled);
    return () => {
      window.removeEventListener('beforeinstallprompt', onBeforeInstall);
      window.removeEventListener('appinstalled', onInstalled);
    };
  }, []);

  const beginInstall = useCallback(() => {
    if (!canEverPrompt() || !('serviceWorker' in navigator)) return;

    void navigator.serviceWorker.getRegistration('/sw.js').then((existing) => {
      // Already registered — by push enrollment, or a previous attempt. Leave it alone;
      // it is not ours to release.
      if (existing) return;
      void navigator.serviceWorker.register('/sw.js').then(
        () => {
          acquired.current = true;
        },
        () => undefined,
      );
    });
  }, []);

  /**
   * Give back the worker if we took it and it went unused.
   *
   * Only ever unregisters one **we** registered, and never when a push subscription exists
   * — enrollment is the other consumer, and tearing that down would silently stop their
   * notifications to tidy up an install dialog they closed.
   */
  const abandonInstall = useCallback(() => {
    if (!acquired.current) return;
    acquired.current = false;

    void navigator.serviceWorker.getRegistration('/sw.js').then(async (registration) => {
      if (!registration) return;
      const subscription = await registration.pushManager.getSubscription().catch(() => null);
      if (subscription) return;
      await registration.unregister().catch(() => undefined);
    });
  }, []);

  const promptInstall = useCallback(async () => {
    if (!deferred) return 'unavailable' as const;
    await deferred.prompt();
    const { outcome } = await deferred.userChoice;
    // Single-use; Chrome fires a fresh one if they become eligible again.
    setDeferred(null);
    if (outcome === 'accepted') {
      acquired.current = false;
    }
    return outcome;
  }, [deferred]);

  const availability: InstallAvailability = installed
    ? 'installed'
    : canEverPrompt()
      ? 'promptable'
      : detectSafariPlatform();

  return { availability, canPromptNow: deferred !== null, beginInstall, abandonInstall, promptInstall };
}
